import {Dialog, DialogPanel, DialogTitle} from "@headlessui/react";
import {XMarkIcon} from "@heroicons/react/24/outline";
import {TrendingUp, Calendar, Target, Clock} from "lucide-react";
import {getMeanEntry, calculateMonthsToTargetWithGrowth} from "../utils/utils";

export const ExpenseShowSidebar = ({
    isOpen,
    setIsOpen,
    expense,
    monthlyIncome,
    monthlyInvestmentPlan,
    withdrawalRate,
    onEditExpense
}) => {
    if (!expense) return null;

    const computeInsight = () => {
        const monthlyCost = getMeanEntry(expense.entries) / 100;
        const amountLeftToCover = Math.max(0, monthlyCost - monthlyIncome);
        
        if (amountLeftToCover === 0) {
            return {
                status: 'covered',
                message: 'Already covered',
                amountLeft: 0
            };
        }

        if (parseFloat(monthlyInvestmentPlan) === 0) {
            return {
                status: 'not_progressing',
                message: 'Not progressing',
                amountLeft: amountLeftToCover
            };
        }

        // Use portfolio growth calculation to determine months needed
        const monthsToCover = calculateMonthsToTargetWithGrowth(
            monthlyIncome,
            monthlyCost,
            parseFloat(monthlyInvestmentPlan),
            withdrawalRate
        );
        
        // Calculate monthly income gain for display purposes
        const monthlyIncomeGain = (monthlyInvestmentPlan * (withdrawalRate / 100)) / 12;
        
        let timeMessage;
        if (monthsToCover <= 12) {
            timeMessage = `about ${monthsToCover} months`;
        } else {
            const years = Math.floor(monthsToCover / 12);
            const remainingMonths = monthsToCover % 12;
            timeMessage = remainingMonths > 0 
                ? `about ${years} years, ${remainingMonths} months`
                : `about ${years} years`;
        }

        return {
            status: 'progressing',
            message: timeMessage,
            amountLeft: amountLeftToCover,
            monthlyGain: monthlyIncomeGain,
            monthsToCover
        };
    };

    const insight = computeInsight();
    const monthlyCost = getMeanEntry(expense.entries) / 100;
    const coverage = Math.min(100, (monthlyIncome / monthlyCost) * 100);

    // Calculate milestones for long-term goals (>5 years)
    const calculateMilestones = () => {
        if (insight.monthsToCover <= 60) return null; // 5 years = 60 months
        
        const oneThirdTarget = monthlyIncome + (monthlyCost - monthlyIncome) / 3;
        const halfTarget = monthlyIncome + (monthlyCost - monthlyIncome) / 2;
        const fullTarget = monthlyCost;
        
        const monthsToOneThird = calculateMonthsToTargetWithGrowth(
            monthlyIncome,
            oneThirdTarget,
            parseFloat(monthlyInvestmentPlan),
            withdrawalRate
        );
        
        const monthsToHalf = calculateMonthsToTargetWithGrowth(
            monthlyIncome,
            halfTarget,
            parseFloat(monthlyInvestmentPlan),
            withdrawalRate
        );
        
        const monthsToFull = calculateMonthsToTargetWithGrowth(
            monthlyIncome,
            fullTarget,
            parseFloat(monthlyInvestmentPlan),
            withdrawalRate
        );
        
        const formatTime = (months) => {
            if (months <= 12) {
                return `about ${months} months`;
            } else {
                const years = Math.floor(months / 12);
                const remainingMonths = months % 12;
                return remainingMonths > 0 
                    ? `about ${years} years, ${remainingMonths} months`
                    : `about ${years} years`;
            }
        };
        
        return {
            oneThird: {
                target: oneThirdTarget,
                time: formatTime(monthsToOneThird),
                months: monthsToOneThird
            },
            half: {
                target: halfTarget,
                time: formatTime(monthsToHalf),
                months: monthsToHalf
            },
            full: {
                target: fullTarget,
                time: formatTime(monthsToFull),
                months: monthsToFull
            }
        };
    };

    const milestones = calculateMilestones();

    const getStatusColor = (status) => {
        switch (status) {
            case 'covered': return 'bg-green-100 text-green-800';
            case 'progressing': return 'bg-blue-100 text-blue-800';
            case 'not_progressing': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'covered': return Target;
            case 'progressing': return TrendingUp;
            case 'not_progressing': return Clock;
            default: return Calendar;
        }
    };

    const StatusIcon = getStatusIcon(insight.status);

    return (
        <Dialog open={isOpen} onClose={setIsOpen} className="relative z-50">
            <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                        <DialogPanel
                            transition
                            className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                        >
                            <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                                <div className="h-0 flex-1 overflow-y-auto">
                                    <div className="bg-indigo-700 px-4 py-6 sm:px-6">
                                        <div className="flex items-center justify-between">
                                            <DialogTitle className="text-base font-semibold leading-6 text-white">
                                                <div className="flex items-center space-x-2">
                                                    <StatusIcon className="w-5 h-5" />
                                                    <span>Insight: {expense.name}</span>
                                                </div>
                                            </DialogTitle>
                                            <div className="ml-3 flex h-7 items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsOpen(false)}
                                                    className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                >
                                                    <span className="absolute -inset-2.5" />
                                                    <span className="sr-only">Close panel</span>
                                                    <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-1">
                                            <p className="text-sm text-indigo-300">
                                                Analysis based on Individual Coverage mode
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-1 flex-col justify-between">
                                        <div className="divide-y divide-gray-200 px-4 sm:px-6">
                                            <div className="space-y-6 pb-5 pt-6">
                                                {/* Current Status */}
                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                    <div className="flex justify-between items-center mb-3">
                                                        <span className="text-sm font-medium text-gray-700">Current Status</span>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(insight.status)}`}>
                                                            {insight.status === 'covered' ? 'Fully Covered' :
                                                             insight.status === 'progressing' ? 'In Progress' : 'Not Progressing'}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <p className="text-gray-500">Monthly Cost</p>
                                                            <p className="font-semibold">${monthlyCost.toFixed(2)}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-gray-500">Coverage</p>
                                                            <p className="font-semibold">{coverage.toFixed(1)}%</p>
                                                        </div>
                                                    </div>
                                                </div>


                                                {/* Time to Cover */}
                                                {milestones && insight.status !== 'covered' && (
                                                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                        <div className="flex items-center space-x-2 mb-3">
                                                            <Calendar className="w-4 h-4 text-blue-600" />
                                                            <span className="text-sm font-medium text-gray-700">Time to Cover</span>
                                                        </div>
                                                        
                                                        <div className="space-y-3">
                                                            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">1/3 Coverage</p>
                                                                    <p className="text-xs text-gray-500">${milestones.oneThird.target.toFixed(2)}/month</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-sm font-semibold text-purple-600">{milestones.oneThird.time}</p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">1/2 Coverage</p>
                                                                    <p className="text-xs text-gray-500">${milestones.half.target.toFixed(2)}/month</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-sm font-semibold text-blue-600">{milestones.half.time}</p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">Full Coverage</p>
                                                                    <p className="text-xs text-gray-500">${milestones.full.target.toFixed(2)}/month</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-sm font-semibold text-green-600">{milestones.full.time}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Recommendations */}
                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <TrendingUp className="w-4 h-4 text-green-600" />
                                                        <span className="text-sm font-medium text-gray-700">Recommendations</span>
                                                    </div>
                                                    
                                                    <div className="text-sm text-gray-600 space-y-2">
                                                        {insight.status === 'covered' && (
                                                            <p>Great job! This expense is fully covered by your portfolio income.</p>
                                                        )}
                                                        {insight.status === 'progressing' && (
                                                            <p>You're making progress! Continue investing ${monthlyInvestmentPlan.toLocaleString()}/month to reach full coverage.</p>
                                                        )}
                                                        {insight.status === 'not_progressing' && (
                                                            <p>Consider setting up a monthly investment plan to make progress toward covering this expense.</p>
                                                        )}
                                                        
                                                        <p className="text-xs text-gray-500 italic">
                                                            *Calculations include 7% inflation-adjusted average market returns
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-shrink-0 justify-between px-4 py-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (onEditExpense) {
                                                onEditExpense(expense);
                                            }
                                            setIsOpen(false);
                                        }}
                                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        Edit Expense
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};
